/**
 * predictive-search-fix.js  v5
 *
 * Replaces PredictiveSearch.getSearchResults() with a Storefront API call.
 *
 * WHY: On kw.mubkhar.com the native /search/suggest endpoint returns HTTP 417
 * for Arabic-character queries due to a Cloudflare WAF rule.
 *
 * v5 strategy for Arabic pages (two sequential fetches):
 *   Step 1 — predictiveSearch WITHOUT @inContext. Shopify’s default search
 *             engine handles all query languages (including Arabic terms that
 *             hit English-indexed fields). Returns correct product IDs.
 *   Step 2 — nodes(ids) WITH @inContext(language: AR). Fetches the
 *             Translate & Adapt Arabic titles for exactly those IDs.
 *   Merge  — Replace English titles with Arabic titles before rendering.
 *
 * English page: single fetch, no language context, Arabic page logic skipped.
 *
 * Theme: "Final Live Theme (Rushdy) - AR Search Fix"
 * Theme ID: gid://shopify/OnlineStoreTheme/188051751197
 */

(function () {
  'use strict';

  var STOREFRONT_URL   = 'https://mubkhar-fragrances.myshopify.com/api/2026-04/graphql.json';
  var STOREFRONT_TOKEN = '25fb217de929dda729b3ca3e2c2277cd';

  var QUERY_SEARCH = 'query PredictiveSearch($query: String!, $first: Int!) { predictiveSearch(query: $query, limit: $first, limitScope: EACH, types: [PRODUCT, COLLECTION, PAGE]) { products { id title handle onlineStoreUrl vendor featuredImage { url(transform: {maxWidth: 400}) altText } priceRange { minVariantPrice { amount currencyCode } } } collections { id title handle onlineStoreUrl } pages { id title handle onlineStoreUrl } } }';

  var QUERY_AR_TITLES = 'query GetArabicTitles($ids: [ID!]!) @inContext(language: AR) { nodes(ids: $ids) { ... on Product { id title } ... on Collection { id title } ... on Page { id title } } }';

  function storefrontFetch(query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (res) {
      if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
      return res.json();
    });
  }

  function renderResults(ps, isRTL, self) {
    var products    = ps.products    || [];
    var collections = ps.collections || [];
    var pages       = ps.pages       || [];
    var html        = '';

    if (products.length) {
      html += '<div data-type-products>';
      html += '<p class="h6 predictive__label">' + (isRTL ? 'المنتجات' : 'Products') + '</p>';
      html += '<ul class="no-bullets">';
      products.forEach(function (p) {
        var cents = Math.round(parseFloat(p.priceRange.minVariantPrice.amount) * 100);
        var price = (window.theme && window.theme.Currency && window.theme.settings)
          ? window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat)
          : p.priceRange.minVariantPrice.amount + ' ' + p.priceRange.minVariantPrice.currencyCode;
        var imgHtml = '';
        if (p.featuredImage) {
          imgHtml = '<div class="image-wrap"><div class="grid__image-ratio grid__image-ratio--square"><img src="' + p.featuredImage.url + '" alt="' + (p.featuredImage.altText || p.title).replace(/"/g, '&quot;') + '" loading="lazy"></div></div>';
        }
        var url = p.onlineStoreUrl || ('/' + (isRTL ? 'ar/' : '') + 'products/' + p.handle);
        html += '<li class="grid__item grid-product small--one-half medium-up--one-quarter"><a href="' + url + '" class="grid-product__link">' + imgHtml + '<div class="grid-product__meta"><p class="grid-product__title">' + p.title + '</p><p class="grid-product__price">' + price + '</p></div></a></li>';
      });
      html += '</ul></div>';
    }

    if (collections.length) {
      var collLabel = (window.theme && window.theme.strings && window.theme.strings.searchCollections)
        ? window.theme.strings.searchCollections
        : (isRTL ? 'التصنيفات' : 'Collections');
      html += '<div data-type-collections><p class="h6 predictive__label">' + collLabel + '</p><ul class="no-bullets">';
      collections.forEach(function (c) {
        var url = c.onlineStoreUrl || ('/' + (isRTL ? 'ar/' : '') + 'collections/' + c.handle);
        html += '<li><a href="' + url + '">' + c.title + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (pages.length) {
      var pageLabel = (window.theme && window.theme.strings && window.theme.strings.searchPages)
        ? window.theme.strings.searchPages
        : (isRTL ? 'الصفحات' : 'Pages');
      html += '<div data-type-pages><p class="h6 predictive__label">' + pageLabel + '</p><ul class="no-bullets">';
      pages.forEach(function (pg) {
        var url = pg.onlineStoreUrl || ('/' + (isRTL ? 'ar/' : '') + 'pages/' + pg.handle);
        html += '<li><a href="' + url + '">' + pg.title + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (!html) {
      html = '<p class="predictive__no-results">' + (isRTL ? 'لا توجد نتائج' : 'No results found') + '</p>';
    }

    self.predictiveSearchResults.innerHTML = html;
    self.open();
  }

  function getSearchResults(searchTerm) {
    var self  = this;
    var isRTL = document.documentElement.dir === 'rtl';

    storefrontFetch(QUERY_SEARCH, { query: searchTerm, first: 3 })
      .then(function (json) {
        if (!json.data || !json.data.predictiveSearch) {
          throw new Error('Unexpected Storefront API response');
        }

        var ps          = json.data.predictiveSearch;
        var products    = ps.products    || [];
        var collections = ps.collections || [];
        var pages       = ps.pages       || [];

        // No results or English page — render immediately
        var noResults = !products.length && !collections.length && !pages.length;
        if (!isRTL || noResults) {
          renderResults(ps, isRTL, self);
          return;
        }

        // Arabic page with results — fetch Arabic titles for these IDs
        var ids = []
          .concat(products.map(function (p) { return p.id; }))
          .concat(collections.map(function (c) { return c.id; }))
          .concat(pages.map(function (pg) { return pg.id; }));

        storefrontFetch(QUERY_AR_TITLES, { ids: ids })
          .then(function (arJson) {
            // Build id → Arabic title map
            var arTitle = {};
            var nodes = (arJson.data || {}).nodes || [];
            nodes.forEach(function (node) {
              if (node && node.id && node.title) arTitle[node.id] = node.title;
            });

            // Overwrite titles with Arabic where available
            products.forEach(function (p) {
              if (arTitle[p.id]) p.title = arTitle[p.id];
            });
            collections.forEach(function (c) {
              if (arTitle[c.id]) c.title = arTitle[c.id];
            });
            pages.forEach(function (pg) {
              if (arTitle[pg.id]) pg.title = arTitle[pg.id];
            });

            renderResults(ps, isRTL, self);
          })
          .catch(function () {
            // Arabic title fetch failed — render with English titles as fallback
            renderResults(ps, isRTL, self);
          });
      })
      .catch(function (err) {
        console.error('[predictive-search-fix] Storefront API error:', err);
        self.close();
      });
  }

  customElements.whenDefined('predictive-search').then(function () {
    var Cls = customElements.get('predictive-search');
    if (!Cls) {
      console.error('[predictive-search-fix] predictive-search element class not found');
      return;
    }
    Cls.prototype.getSearchResults = getSearchResults;
    console.log('[predictive-search-fix] PredictiveSearch.getSearchResults patched ✓ (v5)');
  });

})();
